import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { guardUnsavedBack } from '../../../lib/formGuard';
import type { PresentingComplaint } from '../../../types/prf';

interface PresentingComplaintSectionProps {
  data: PresentingComplaint;
  onSave: (data: PresentingComplaint) => void;
  onBack: () => void;
}

export default function PresentingComplaintSection({
  data,
  onSave,
  onBack,
}: PresentingComplaintSectionProps) {
  const [formData, setFormData] = useState<PresentingComplaint>(data);

  const handleChange = (field: keyof PresentingComplaint, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleBackClick = () => guardUnsavedBack(data, formData, onBack);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackClick}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-foreground">Presenting Complaint</h2>
              <p className="text-xs text-muted-foreground">Chief complaint and injury details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Chief Complaint */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Chief Complaint
            </label>
            <textarea
              value={formData.chiefComplaint || ''}
              onChange={(e) => handleChange('chiefComplaint', e.target.value)}
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="Describe the chief complaint..."
            />
          </div>

          {/* Onset Time */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Onset Time
            </label>
            <input
              type="text"
              value={formData.onsetTime || ''}
              onChange={(e) => handleChange('onsetTime', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="Eg: 14:30"
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Duration
            </label>
            <input
              type="text"
              value={formData.duration || ''}
              onChange={(e) => handleChange('duration', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="Eg: 2 hours"
            />
          </div>

          {/* Severity */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Severity
            </label>
            <input
              type="text"
              value={formData.severity || ''}
              onChange={(e) => handleChange('severity', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
              placeholder="Eg: Mild, Moderate, Severe"
            />
          </div>

          {/* Symptoms */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Symptoms
            </label>
            <textarea
              value={Array.isArray(formData.symptoms) ? formData.symptoms.join('\n') : formData.symptoms || ''}
              onChange={(e) => handleChange('symptoms', e.target.value.split('\n').filter(s => s.trim()))}
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="Enter symptoms (one per line)"
            />
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Additional Notes
            </label>
            <textarea
              value={formData.additionalNotes || ''}
              onChange={(e) => handleChange('additionalNotes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg bg-background border-2 border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              placeholder="Any additional information..."
            />
          </div>
        </div>
      </div>

      {/* Footer with Save Button */}
      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          onClick={handleSave}
          className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
      </div>
    </div>
  );
}
